import { useMemo, useState } from 'react'
import {
  Badge,
  Box,
  Button,
  CloseButton,
  Dialog,
  HStack,
  Input,
  NativeSelect,
  Spinner,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react';
import { FiFileText } from 'react-icons/fi'
import { useOutreach, useSaveOutreach } from '../../hooks/useLobbyDay'
import type { LegislatorOutreachRowDto, OutreachStatus } from '../../types/lobbyDay'
import { CHAMBER_FILTER_ANY, matchesChamberFilter } from './chamberFilter'
import { ChamberFilterSelect } from './ChamberFilterSelect'
import { LegislatorFormModal } from './LegislatorFormModal'
import { LegislatorNotesPanel } from './LegislatorNotesPanel'
import { ResponsiveModal } from '../shared'

const STATUS_OPTIONS: { value: OutreachStatus; label: string; color: string }[] = [
  { value: 'NotContacted', label: 'Not contacted', color: 'gray' },
  { value: 'Requested', label: 'Requested (AP)', color: 'blue' },
  { value: 'Scheduled', label: 'Scheduled', color: 'green' },
  { value: 'OnHold', label: 'On hold', color: 'yellow' },
  { value: 'Declined', label: 'Declined', color: 'red' },
]

export function OutreachTab({ eventId }: { eventId: string }) {
  const { data: rows, isLoading, error } = useOutreach(eventId)
  const saveMutation = useSaveOutreach(eventId)
  const [chamberFilter, setChamberFilter] = useState(CHAMBER_FILTER_ANY)
  const [notesLogRow, setNotesLogRow] = useState<LegislatorOutreachRowDto | null>(null)
  const [modalLegislatorId, setModalLegislatorId] = useState<string | null>(null)

  // Rows arrive unpaginated and already carry `chamber`, so this filter is in-memory.
  const visibleRows = useMemo(
    () => (rows ?? []).filter((r) => matchesChamberFilter(r.chamber, chamberFilter)),
    [rows, chamberFilter],
  )

  const save = (row: LegislatorOutreachRowDto, patch: Partial<LegislatorOutreachRowDto>) =>
    saveMutation.mutate({
      legislatorId: row.legislatorId,
      data: {
        status: (patch.status ?? row.status) as OutreachStatus,
        lastContactedOn: patch.lastContactedOn !== undefined ? patch.lastContactedOn : row.lastContactedOn,
        notes: patch.notes !== undefined ? patch.notes : row.notes,
      },
    })

  if (isLoading) return <VStack py={12}><Spinner size="lg" color="accent" /></VStack>
  if (error) return <Text color="red.500">Failed to load outreach.</Text>

  return (
    <Box>
      <Text color="gray.600" mb={4}>
        Office-by-office booking tracker. Meeting times appear from the Schedule tab; tentative
        ones are meetings that exist but aren’t confirmed yet.
      </Text>
      <HStack mb={3} gap={3} flexWrap="wrap">
        <ChamberFilterSelect value={chamberFilter} onChange={setChamberFilter} />
        {chamberFilter !== CHAMBER_FILTER_ANY && (
          <Text fontSize="sm" color="gray.600">
            Showing {visibleRows.length} of {(rows ?? []).length}
          </Text>
        )}
      </HStack>
      <Box bg="surface" borderRadius="lg" shadow="md" overflowX="auto">
        <Table.Root size="sm">
          <Table.Header bg="gray.50">
            <Table.Row>
              <Table.ColumnHeader>Legislator</Table.ColumnHeader><Table.ColumnHeader>District</Table.ColumnHeader><Table.ColumnHeader>Office</Table.ColumnHeader><Table.ColumnHeader>Phone</Table.ColumnHeader><Table.ColumnHeader>Email</Table.ColumnHeader>
              <Table.ColumnHeader>Status</Table.ColumnHeader><Table.ColumnHeader>Contacted</Table.ColumnHeader><Table.ColumnHeader>Meeting</Table.ColumnHeader><Table.ColumnHeader>Notes</Table.ColumnHeader><Table.ColumnHeader>Notes log</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {visibleRows.map((r) => (
              <Table.Row key={r.legislatorId}>
                <Table.Cell fontWeight="medium" whiteSpace="nowrap">
                  <Text as="button" textAlign="left"
                        color="accent" cursor="pointer" _hover={{ textDecoration: 'underline' }}
                        onClick={() => setModalLegislatorId(r.legislatorId)}
                        {...({ type: 'button' } as Record<string, unknown>)}>
                    {r.fullName}
                  </Text>
                  {r.party && <Text as="span" color="gray.500"> ({r.party[0]})</Text>}
                </Table.Cell>
                <Table.Cell maxW="220px"><Text lineClamp={1}>{r.district}</Text></Table.Cell>
                <Table.Cell whiteSpace="nowrap">{r.officeRoom ?? '—'}</Table.Cell>
                <Table.Cell whiteSpace="nowrap">{r.phone ?? '—'}</Table.Cell>
                <Table.Cell maxW="200px"><Text lineClamp={1}>{r.email ?? '—'}</Text></Table.Cell>
                <Table.Cell>
                  <NativeSelect.Root size="xs">
                    <NativeSelect.Field
                      w="140px"
                      value={r.status}
                      onChange={(e) => save(r, { status: e.target.value as OutreachStatus })}>
                      {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                </Table.Cell>
                <Table.Cell>
                  <Input size="xs" type="date" w="130px" value={r.lastContactedOn ?? ''}
                    onChange={(e) => save(r, { lastContactedOn: e.target.value || null })} />
                </Table.Cell>
                <Table.Cell>
                  <HStack gap={1} flexWrap="wrap">
                    {r.meetingTimes.map((t, i) => (
                      <Badge
                        key={i}
                        colorPalette={t.status === 'Tentative' ? 'gray' : 'green'}
                        variant={t.status === 'Tentative' ? 'outline' : 'solid'}>
                        {t.status === 'Tentative' ? `${t.time} · tentative` : t.time}
                      </Badge>
                    ))}
                    {r.meetingTimes.length === 0 && r.status === 'Scheduled' && (
                      <Badge colorPalette="yellow">no meeting yet</Badge>
                    )}
                  </HStack>
                </Table.Cell>
                <Table.Cell>
                  <Input size="xs" w="180px" defaultValue={r.notes ?? ''} placeholder="TBD, staff only…"
                    onBlur={(e) => { if (e.target.value !== (r.notes ?? '')) save(r, { notes: e.target.value || null }) }} />
                </Table.Cell>
                <Table.Cell>
                  <Button
                    type="button"
                    size="sm"
                    minH="44px"
                    minW="44px"
                    px={2}
                    variant="ghost"
                    colorPalette="brand"
                    aria-label={`Notes log for ${r.fullName}`}
                    onClick={() => setNotesLogRow(r)}
                  >
                    <FiFileText />
                  </Button>
                </Table.Cell>
              </Table.Row>
            ))}
            {visibleRows.length === 0 && (
              <Table.Row><Table.Cell colSpan={10}><Text py={6} textAlign="center" color="gray.500">
                {(rows ?? []).length === 0
                  ? 'No legislators in the roster yet — add them under Lobby Day → Legislators.'
                  : 'No legislators match this filter.'}
              </Text></Table.Cell></Table.Row>
            )}
          </Table.Body>
        </Table.Root>
      </Box>
      <ResponsiveModal isOpen={!!notesLogRow} onClose={() => setNotesLogRow(null)} size="lg">
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>Notes log{notesLogRow ? ` — ${notesLogRow.fullName}` : ''}</Dialog.Header>
            <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
            <Dialog.Body pb={6}>
              {notesLogRow && (
                <LegislatorNotesPanel legislatorId={notesLogRow.legislatorId} eventId={eventId} />
              )}
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </ResponsiveModal>
      <LegislatorFormModal
        isOpen={!!modalLegislatorId}
        legislatorId={modalLegislatorId ?? undefined}
        onClose={() => setModalLegislatorId(null)}
      />
    </Box>
  );
}
